/**
 * API Keys management page
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ExtensionInstructions } from '../../components/ExtensionInstructions';

// Importar la configuración centralizada de API
const API_BASE_URL =
  (import.meta as any).env?.VITE_API_URL || 'http://localhost:8000/api/v1';

interface APIKey {
  id: string;
  name: string;
  description?: string;
  key_prefix: string;
  permissions: string;
  is_active: boolean;
  last_used_at?: string;
  created_at: string;
}

interface NewAPIKey extends APIKey {
  api_key: string; // Solo se muestra una vez al crear
}

export default function ApiKeysPage() {
  const { getToken } = useAuth();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyData, setNewKeyData] = useState({
    name: '',
    description: '',
    permissions: 'read,write',
  });
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Cargar API Keys
  const loadApiKeys = async () => {
    try {
      console.log('🔑 Iniciando carga de API Keys...');
      const token = await getToken();

      if (!token) {
        console.error('❌ No se pudo obtener el token de Clerk');
        setError('Error de autenticación: No se pudo obtener el token');
        return;
      }

      console.log(
        '✅ Token obtenido de Clerk:',
        token.substring(0, 20) + '...'
      );

      const response = await fetch(`${API_BASE_URL}/api-keys/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 Respuesta del servidor:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ API Keys cargadas exitosamente:', data);
        setApiKeys(data.api_keys);
      } else {
        const errorText = await response.text();
        console.error('❌ Error del servidor:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        throw new Error(
          `Error ${response.status}: ${errorText || 'Error al cargar API Keys'}`
        );
      }
    } catch (error) {
      console.error('❌ Error completo loading API keys:', error);
      setError(
        error instanceof Error ? error.message : 'Error al cargar las API Keys'
      );
    } finally {
      setLoading(false);
    }
  };

  // Crear nueva API Key
  const createApiKey = async () => {
    if (!newKeyData.name.trim()) {
      setError('El nombre es requerido');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/api-keys/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newKeyData),
      });

      if (response.ok) {
        const newKey: NewAPIKey = await response.json();
        setCreatedKey(newKey.api_key);
        setApiKeys(prev => [newKey, ...prev]);
        setShowCreateForm(false);
        setNewKeyData({ name: '', description: '', permissions: 'read,write' });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al crear API Key');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      setError(
        error instanceof Error ? error.message : 'Error al crear API Key'
      );
    } finally {
      setCreating(false);
    }
  };

  // Eliminar API Key
  const deleteApiKey = async (keyId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta API Key?')) {
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setApiKeys(prev => prev.filter(key => key.id !== keyId));
      } else {
        throw new Error('Error al eliminar API Key');
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
      setError('Error al eliminar la API Key');
    }
  };

  // Alternar estado activo/inactivo
  const toggleApiKey = async (keyId: string, isActive: boolean) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/api-keys/${keyId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !isActive }),
      });

      if (response.ok) {
        setApiKeys(prev =>
          prev.map(key =>
            key.id === keyId ? { ...key, is_active: !isActive } : key
          )
        );
      } else {
        throw new Error('Error al actualizar API Key');
      }
    } catch (error) {
      console.error('Error toggling API key:', error);
      setError('Error al actualizar la API Key');
    }
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-96'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto'></div>
          <p className='mt-2 text-gray-600'>Cargando API Keys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-6xl mx-auto p-6'>
      <div className='mb-8'>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>
          API Keys
        </h1>
        <p className='text-gray-600 dark:text-gray-400 mt-2'>
          Gestiona las API Keys para conectar la extensión de Chrome con tu
          cuenta.
        </p>
      </div>

      {/* Instrucciones para usar la extensión */}
      <ExtensionInstructions />

      {/* Mostrar API Key recién creada */}
      {createdKey && (
        <div className='mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg'>
          <h3 className='text-lg font-semibold text-green-800 dark:text-green-200 mb-2'>
            ✅ API Key creada exitosamente
          </h3>
          <p className='text-sm text-green-700 dark:text-green-300 mb-3'>
            Copia esta API Key ahora. No podrás verla de nuevo por seguridad.
          </p>
          <div className='flex items-center gap-2'>
            <code className='flex-1 p-3 bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded font-mono text-sm'>
              {createdKey}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(createdKey);
                alert('API Key copiada al portapapeles');
              }}
              className='px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors'
            >
              Copiar
            </button>
          </div>
          <button
            onClick={() => setCreatedKey(null)}
            className='mt-3 text-sm text-green-600 dark:text-green-400 hover:underline'
          >
            Entendido, ocultar
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className='mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg'>
          <p className='text-red-800 dark:text-red-200'>{error}</p>
          <button
            onClick={() => setError(null)}
            className='mt-2 text-sm text-red-600 dark:text-red-400 hover:underline'
          >
            Cerrar
          </button>
        </div>
      )}

      {/* Botón crear nueva API Key */}
      <div className='mb-6'>
        <button
          onClick={() => setShowCreateForm(true)}
          disabled={apiKeys.length >= 5}
          className='px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
        >
          + Nueva API Key
        </button>
        {apiKeys.length >= 5 && (
          <p className='text-sm text-gray-500 mt-2'>
            Máximo 5 API Keys por usuario. Elimina una existente para crear una
            nueva.
          </p>
        )}
      </div>

      {/* Formulario crear API Key */}
      {showCreateForm && (
        <div className='mb-6 p-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg'>
          <h3 className='text-lg font-semibold mb-4'>Crear nueva API Key</h3>

          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                Nombre *
              </label>
              <input
                type='text'
                value={newKeyData.name}
                onChange={e =>
                  setNewKeyData(prev => ({ ...prev, name: e.target.value }))
                }
                placeholder='Ej: Extensión Chrome - Trabajo'
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white'
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                Descripción
              </label>
              <input
                type='text'
                value={newKeyData.description}
                onChange={e =>
                  setNewKeyData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder='Descripción opcional'
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white'
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                Permisos
              </label>
              <select
                value={newKeyData.permissions}
                onChange={e =>
                  setNewKeyData(prev => ({
                    ...prev,
                    permissions: e.target.value,
                  }))
                }
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white'
              >
                <option value='read'>Solo lectura</option>
                <option value='read,write'>Lectura y escritura</option>
              </select>
            </div>
          </div>

          <div className='flex gap-3 mt-6'>
            <button
              onClick={createApiKey}
              disabled={creating || !newKeyData.name.trim()}
              className='px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
            >
              {creating ? 'Creando...' : 'Crear API Key'}
            </button>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setNewKeyData({
                  name: '',
                  description: '',
                  permissions: 'read,write',
                });
              }}
              className='px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors'
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de API Keys */}
      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden'>
        {apiKeys.length === 0 ? (
          <div className='p-8 text-center'>
            <div className='text-gray-400 text-6xl mb-4'>🔑</div>
            <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-2'>
              No tienes API Keys
            </h3>
            <p className='text-gray-600 dark:text-gray-400 mb-4'>
              Crea tu primera API Key para conectar la extensión de Chrome.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className='px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors'
            >
              Crear primera API Key
            </button>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-gray-50 dark:bg-gray-700'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    Nombre
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    Key
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    Permisos
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    Estado
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    Último uso
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                {apiKeys.map(apiKey => (
                  <tr key={apiKey.id}>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div>
                        <div className='text-sm font-medium text-gray-900 dark:text-white'>
                          {apiKey.name}
                        </div>
                        {apiKey.description && (
                          <div className='text-sm text-gray-500 dark:text-gray-400'>
                            {apiKey.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <code className='text-sm font-mono text-gray-600 dark:text-gray-300'>
                        {apiKey.key_prefix}
                      </code>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                        {apiKey.permissions}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${apiKey.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}
                      >
                        {apiKey.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                      {apiKey.last_used_at
                        ? formatDate(apiKey.last_used_at)
                        : 'Nunca'}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2'>
                      <button
                        onClick={() =>
                          toggleApiKey(apiKey.id, apiKey.is_active)
                        }
                        className={`${apiKey.is_active
                          ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                          : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                          }`}
                      >
                        {apiKey.is_active ? 'Desactivar' : 'Activar'}
                      </button>
                      <button
                        onClick={() => deleteApiKey(apiKey.id)}
                        className='text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Instrucciones */}
      <div className='mt-8 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg'>
        <h3 className='text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3'>
          📋 Cómo usar tu API Key
        </h3>
        <ol className='list-decimal list-inside space-y-2 text-sm text-blue-700 dark:text-blue-300'>
          <li>Copia la API Key cuando la crees (solo se muestra una vez)</li>
          <li>Abre la extensión de Chrome de Grade</li>
          <li>Ve a la configuración y pega tu API Key</li>
          <li>¡Listo! La extensión estará conectada a tu cuenta</li>
        </ol>
        <p className='mt-3 text-xs text-blue-600 dark:text-blue-400'>
          💡 Tip: Puedes crear múltiples API Keys para diferentes dispositivos o
          propósitos.
        </p>
      </div>
    </div>
  );
}
